import { use, useCallback, useState } from 'react';
import type { PoolOfferDetail } from '../api';
import { cacheGameKind } from '../game/gameKindCache';
// The context itself rather than `useSession`, which throws without a provider: the hook runs outside one in tests.
import { SessionContext } from '../session/SessionContext';
import { customPackLabUrl } from './labUrl';

export interface LabHandoff {
  /** The page is on its way to the lab and must show no loot-pack UI meanwhile. */
  leavingForLab: boolean;
  /** Sends the page to the lab if `offer` is a custom pack, and says whether it did; any other offer is left alone. */
  leaveIfCustomPack(offer: PoolOfferDetail): boolean;
}

/**
 * For the loot-pack pages' data hooks, where every way onto a pack page ends up: a custom pack gets there from a
 * launch the tab has not learned it on (the boot looks nothing up for those), an in-app swap or a demo link, and only
 * the lab, which a full load boots, can play it. The kind is stored first, since the next boot opens the lab only
 * for a game it knows to be one; a tab that stores nothing gets the kind, and the session, in the lab's address
 * instead, or that load would forget both and come straight back here. The page is replaced rather than left in the
 * history: it is no place to come back to.
 */
export function useLeaveForLab(): LabHandoff {
  const [leavingForLab, setLeavingForLab] = useState(false);
  const getToken = use(SessionContext)?.getToken;

  const leaveIfCustomPack = useCallback(
    (offer: PoolOfferDetail): boolean => {
      if (offer.gameKind !== 'custom_pack') return false;
      const gameId = String(offer.id);
      const carry = cacheGameKind(gameId, 'custom_pack') ? undefined : { sessionToken: getToken?.() ?? null };
      window.location.replace(customPackLabUrl(window.location.href, gameId, carry));
      setLeavingForLab(true);
      return true;
    },
    [getToken],
  );

  return { leavingForLab, leaveIfCustomPack };
}
